/**
 * ProjectForm (FE-3 §6.2)
 *
 * Form di creazione/modifica progetto. Usato dentro Dialog.
 *
 *  - Campi: nome (obbligatorio), descrizione, palette colori, icona opzionale;
 *  - Modalità colonne: base | personalizzata | copia da progetto;
 *  - Personalizzata: righe nome/colore, add/remove, drag-riordino (dnd-kit),
 *    validazione nomi vuoti/duplicati prima del submit;
 *  - Copia: select progetto sorgente; il submit invia copy_columns_from_project_id;
 *  - In modifica aggiorna solo metadati: la gestione colonne resta sulla board
 *    o nel pannello progetto (no due flussi concorrenti).
 *
 * Il componente è controllato su `open`/`onOpenChange`. `onSubmit` riceve il
 * payload coerente con CreateProjectPayload/UpdateProjectPayload.
 */

import { useEffect, useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  GripVertical,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Button } from "../../ui/button";
import type {
  CreateProjectPayload,
  TaskProject,
  UpdateProjectPayload,
} from "../../../types/tasks";

const PROJECT_COLORS = [
  "#7b3fff",
  "#3b82f6",
  "#22c55e",
  "#f59e0b",
  "#ef4444",
  "#ec4899",
  "#14b8a6",
  "#6b7280",
];

const COLUMN_COLORS = [
  "#8b5cf6",
  "#3b82f6",
  "#22c55e",
  "#f59e0b",
  "#ef4444",
  "#14b8a6",
];

const ICON_OPTIONS = ["folder", "sparkles"];

type ColumnMode = "base" | "custom" | "copy";

interface ColumnDraft {
  id: string; // ID locale per dnd-kit
  name: string;
  color: string;
}

export interface ProjectFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Progetto in edit; null in creazione. */
  project?: TaskProject | null;
  /** Lista progetti attivi, per la select "copia da". */
  copySourceProjects?: TaskProject[];
  /** Submit: crea o aggiorna. Solleva errore testuale per mostrarlo inline. */
  onSubmit: (
    payload: CreateProjectPayload | UpdateProjectPayload,
    isEdit: boolean
  ) => Promise<void>;
}

export default function ProjectForm({
  open,
  onOpenChange,
  project,
  copySourceProjects = [],
  onSubmit,
}: ProjectFormProps) {
  const isEdit = !!project;
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState(PROJECT_COLORS[0]);
  const [icon, setIcon] = useState("folder");
  const [mode, setMode] = useState<ColumnMode>("base");
  const [columns, setColumns] = useState<ColumnDraft[]>([
    { id: "c1", name: "", color: COLUMN_COLORS[0] },
  ]);
  const [copyFromId, setCopyFromId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset/Riempi quando si apre o cambia project.
  useEffect(() => {
    if (!open) return;
    setError(null);
    if (project) {
      setName(project.name);
      setDescription(project.description || "");
      setColor(project.color || PROJECT_COLORS[0]);
      setIcon(project.icon || "folder");
      setMode("base");
      setColumns([{ id: "c1", name: "", color: COLUMN_COLORS[0] }]);
      setCopyFromId(null);
    } else {
      setName("");
      setDescription("");
      setColor(PROJECT_COLORS[0]);
      setIcon("folder");
      setMode("base");
      setColumns([{ id: "c1", name: "", color: COLUMN_COLORS[0] }]);
      setCopyFromId(null);
    }
  }, [open, project]);

  // Validazione nomi colonne personalizzate.
  const columnErrors = useMemo(() => {
    const errs: Record<string, string> = {};
    if (mode !== "custom") return errs;
    const seen = new Set<string>();
    for (const c of columns) {
      const key = c.name.trim().toLowerCase();
      if (!key) {
        errs[c.id] = "Nome obbligatorio";
      } else if (seen.has(key)) {
        errs[c.id] = "Duplicato";
      } else {
        seen.add(key);
      }
    }
    return errs;
  }, [columns, mode]);

  const hasColumnErrors = Object.keys(columnErrors).length > 0;

  const addColumn = () => {
    setColumns((prev) => [
      ...prev,
      {
        id: `c${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        name: "",
        color: COLUMN_COLORS[prev.length % COLUMN_COLORS.length],
      },
    ]);
  };

  const removeColumn = (id: string) => {
    // Non eliminare l'ultima riga (deve esistere almeno una colonna).
    setColumns((prev) => (prev.length <= 1 ? prev : prev.filter((c) => c.id !== id)));
  };

  const updateColumn = (id: string, patch: Partial<ColumnDraft>) => {
    setColumns((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  };

  // dnd-kit setup
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setColumns((prev) => {
      const fromIdx = prev.findIndex((c) => c.id === active.id);
      const toIdx = prev.findIndex((c) => c.id === over.id);
      if (fromIdx === -1 || toIdx === -1) return prev;
      return arrayMove(prev, fromIdx, toIdx);
    });
  };

  const canSubmit = (() => {
    if (!name.trim()) return false;
    if (!isEdit) {
      if (mode === "custom") {
        if (columns.length === 0) return false;
        if (hasColumnErrors) return false;
      } else if (mode === "copy") {
        if (copyFromId == null) return false;
      }
    }
    return true;
  })();

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit) {
        const payload: UpdateProjectPayload = {
          name: name.trim(),
          description: description.trim(),
          color,
          icon,
        };
        await onSubmit(payload, true);
      } else {
        const payload: CreateProjectPayload = {
          name: name.trim(),
          description: description.trim(),
          color,
          icon,
        };
        if (mode === "custom") {
          payload.columns = columns
            .map((c) => ({ name: c.name.trim(), color: c.color }))
            .filter((c) => c.name.trim());
        } else if (mode === "copy" && copyFromId != null) {
          payload.copy_columns_from_project_id = copyFromId;
        }
        // mode === "base" → nessun campo columns/copy: il BE usa default
        await onSubmit(payload, false);
      }
      // Successo: chiude il dialog. Se il caller vuole restare aperto (es.
      // per creare un altro progetto), può evitarlo gestendo onOpenChange.
      onOpenChange(false);
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ||
        (err instanceof Error ? err.message : "Salvataggio non riuscito.");
      setError(detail);
      // Non chiudere su errore.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] bg-[var(--hexa-modal-bg)] border-[var(--hexa-border)] text-[var(--hexa-text-primary)]">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Modifica progetto" : "Nuovo progetto"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Aggiorna nome, descrizione, colore e icona."
              : "Crea un progetto e definisci le colonne iniziali."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Nome */}
          <div className="space-y-1">
            <label className="text-xs font-medium text-[var(--hexa-text-secondary)]">
              Nome <span className="text-[#ef4444]">*</span>
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Es. Sviluppo HexisAI"
              autoFocus
              className="h-9 bg-[var(--hexa-card-bg)] border-[rgba(106,77,255,0.25)] text-sm"
            />
          </div>

          {/* Descrizione */}
          <div className="space-y-1">
            <label className="text-xs font-medium text-[var(--hexa-text-secondary)]">
              Descrizione
            </label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Breave descrizione (opzionale)"
              className="h-9 bg-[var(--hexa-card-bg)] border-[rgba(106,77,255,0.25)] text-sm"
            />
          </div>

          {/* Colore */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[var(--hexa-text-secondary)]">
              Colore
            </label>
            <div className="flex items-center gap-1.5">
              {PROJECT_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`w-6 h-6 rounded-full transition ${
                    color === c ? "ring-2 ring-offset-2 ring-offset-[var(--hexa-modal-bg)]" : ""
                  }`}
                  style={{ backgroundColor: c, boxShadow: color === c ? `0 0 0 2px ${c}` : undefined }}
                  aria-label={`Colore ${c}`}
                  aria-pressed={color === c}
                />
              ))}
            </div>
          </div>

          {/* Icona */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[var(--hexa-text-secondary)]">
              Icona (opzionale)
            </label>
            <div className="flex items-center gap-1.5">
              {ICON_OPTIONS.map((ic) => (
                <button
                  key={ic}
                  type="button"
                  onClick={() => setIcon(ic)}
                  className={`px-2.5 h-7 rounded-md text-xs font-medium border transition ${
                    icon === ic
                      ? "border-[var(--hexa-purple)] text-[var(--hexa-purple)] bg-[var(--hexa-purple-glow)]"
                      : "border-[rgba(106,77,255,0.25)] text-[var(--hexa-text-primary)] hover:border-[var(--hexa-purple)]"
                  }`}
                  aria-pressed={icon === ic}
                >
                  {ic}
                </button>
              ))}
            </div>
          </div>

          {/* Struttura colonne (solo in creazione) */}
          {!isEdit && (
            <div className="space-y-2">
              <label className="text-xs font-medium text-[var(--hexa-text-secondary)]">
                Struttura colonne
              </label>
              <div className="flex items-center gap-1">
                {(
                  [
                    { v: "base", label: "Base" },
                    { v: "custom", label: "Personalizzata" },
                    { v: "copy", label: "Copia" },
                  ] as Array<{ v: ColumnMode; label: string }>
                ).map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setMode(opt.v)}
                    className={`flex-1 h-8 text-xs font-medium rounded-md border transition ${
                      mode === opt.v
                        ? "border-[var(--hexa-purple)] text-[var(--hexa-purple)] bg-[var(--hexa-purple-glow)]"
                        : "border-[rgba(106,77,255,0.25)] text-[var(--hexa-text-primary)] hover:border-[var(--hexa-purple)]"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {mode === "base" && (
                <p className="text-[11px] text-[var(--hexa-text-muted)] px-1">
                  Verranno create le colonne predefinite: Da fare, In corso, Completato.
                </p>
              )}

              {mode === "custom" && (
                <CustomColumnsEditor
                  columns={columns}
                  errors={columnErrors}
                  sensors={sensors}
                  onDragEnd={handleDragEnd}
                  onUpdate={updateColumn}
                  onAdd={addColumn}
                  onRemove={removeColumn}
                />
              )}

              {mode === "copy" && (
                <div className="space-y-1">
                  <select
                    value={copyFromId ?? ""}
                    onChange={(e) =>
                      setCopyFromId(
                        e.target.value === "" ? null : Number(e.target.value)
                      )
                    }
                    className="w-full h-9 px-2 rounded-md bg-[var(--hexa-card-bg)] border border-[rgba(106,77,255,0.25)] text-sm text-[var(--hexa-text-primary)]"
                  >
                    <option value="">Seleziona un progetto sorgente...</option>
                    {copySourceProjects
                      .filter((p) => p.id !== project?.id)
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                  </select>
                  {copySourceProjects.length === 0 && (
                    <p className="text-[11px] text-[var(--hexa-text-muted)] px-1">
                      Non ci sono progetti da cui copiare.
                    </p>
                  )}
                  <p className="text-[11px] text-[var(--hexa-text-muted)] px-1">
                    Verranno copiate nome, colore e ordine delle colonne. I task non vengono duplicati.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {error && (
          <div className="text-xs text-[#ef4444] bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.3)] rounded-md px-3 py-2">
            {error}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            className="text-sm h-8"
          >
            Annulla
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            className="text-sm h-8 bg-[var(--hexa-purple)] hover:bg-[var(--hexa-purple-hover)] text-white"
          >
            {submitting ? "Salvataggio..." : isEdit ? "Salva" : "Crea progetto"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface CustomColumnsEditorProps {
  columns: ColumnDraft[];
  errors: Record<string, string>;
  sensors: ReturnType<typeof useSensors>;
  onDragEnd: (e: DragEndEvent) => void;
  onUpdate: (id: string, patch: Partial<ColumnDraft>) => void;
  onAdd: () => void;
  onRemove: (id: string) => void;
}

function CustomColumnsEditor({
  columns,
  errors,
  sensors,
  onDragEnd,
  onUpdate,
  onAdd,
  onRemove,
}: CustomColumnsEditorProps) {
  return (
    <div className="space-y-1.5">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={columns.map((c) => c.id)}
          strategy={verticalListSortingStrategy}
        >
          {columns.map((c, idx) => (
            <SortableColumnRow
              key={c.id}
              column={c}
              error={errors[c.id]}
              isFirst={idx === 0}
              isLast={idx === columns.length - 1}
              canRemove={columns.length > 1}
              onUpdate={onUpdate}
              onRemove={onRemove}
            />
          ))}
        </SortableContext>
      </DndContext>
      <button
        type="button"
        onClick={onAdd}
        className="inline-flex items-center gap-1 text-xs text-[var(--hexa-purple)] hover:underline mt-1"
      >
        <Plus className="h-3 w-3" />
        Aggiungi colonna
      </button>
    </div>
  );
}

interface SortableColumnRowProps {
  column: ColumnDraft;
  error?: string;
  isFirst: boolean;
  isLast: boolean;
  canRemove: boolean;
  onUpdate: (id: string, patch: Partial<ColumnDraft>) => void;
  onRemove: (id: string) => void;
}

function SortableColumnRow({
  column,
  error,
  canRemove,
  onUpdate,
  onRemove,
}: SortableColumnRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: column.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-1.5 px-1.5 py-1 rounded-md border ${
        error
          ? "border-[rgba(239,68,68,0.4)] bg-[rgba(239,68,68,0.05)]"
          : "border-[var(--hexa-border-subtle)] bg-[var(--hexa-card-bg)]"
      }`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-[var(--hexa-text-muted)] hover:text-[var(--hexa-text-primary)] touch-none"
        aria-label="Trascina per riordinare"
      >
        <GripVertical className="h-3.5 w-3.5" />
      </button>
      <Input
        value={column.name}
        onChange={(e) => onUpdate(column.id, { name: e.target.value })}
        placeholder="Nome colonna"
        className="h-7 flex-1 bg-transparent border-0 text-sm focus-visible:ring-0 focus-visible:ring-offset-0"
      />
      <div className="flex items-center gap-0.5">
        {COLUMN_COLORS.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => onUpdate(column.id, { color: c })}
            className={`w-4 h-4 rounded-full transition ${
              column.color === c ? "ring-1 ring-offset-1 ring-offset-[var(--hexa-card-bg)]" : ""
            }`}
            style={{
              backgroundColor: c,
              boxShadow: column.color === c ? `0 0 0 1px ${c}` : undefined,
            }}
            aria-label={`Colore ${c}`}
            aria-pressed={column.color === c}
          />
        ))}
      </div>
      {canRemove && (
        <button
          type="button"
          onClick={() => onRemove(column.id)}
          className="text-[var(--hexa-text-muted)] hover:text-[#ef4444] transition p-1"
          aria-label="Rimuovi colonna"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

// Helper esportato per normalizzare messaggi di errore dal BE (usato dai caller).
export function describeProjectError(err: unknown): string {
  const data = (err as { response?: { data?: Record<string, unknown> } })?.response
    ?.data;
  if (!data) return err instanceof Error ? err.message : "Errore sconosciuto.";
  const code = data.code as string | undefined;
  if (code === "duplicate_project_name")
    return "Esiste già un progetto con questo nome.";
  if (code === "duplicate_column_name")
    return "Esiste già una colonna con questo nome nel progetto.";
  if (code === "project_archived") return "Il progetto è archiviato.";
  if (typeof data.detail === "string") return data.detail;
  // field_errors
  const fe = data.field_errors as Record<string, string[]> | undefined;
  if (fe) {
    const firstKey = Object.keys(fe)[0];
    if (firstKey && Array.isArray(fe[firstKey]) && fe[firstKey].length) {
      return String(fe[firstKey][0]);
    }
  }
  return "Salvataggio non riuscito.";
}

// Re-export per permettere ai caller di mostrare toast.
export const projectFormToast = {
  created: (name: string) => toast.success(`Progetto "${name}" creato`),
  updated: (name: string) => toast.success(`Progetto "${name}" aggiornato`),
  archived: (name: string) => toast.success(`Progetto "${name}" archiviato`),
  restored: (name: string) => toast.success(`Progetto "${name}" ripristinato`),
};

// Silence unused import warnings (kept for clarity in ICON_OPTIONS extensions).
void ChevronDown;
void ChevronUp;
void X;
